import React, { useEffect, useState } from 'react';
import { Button, Input, InputOnChangeData, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Checkmark20Filled, Dismiss20Filled } from '@fluentui/react-icons';
import { Breakpoints, customColors } from '../../../styles';

const useStyles = makeStyles({
    root: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    input: {
        width: 'max(120px, 60%)',
        minHeight: '28px',
    },
    buttons: {
        display: 'flex',
        marginLeft: tokens.spacingHorizontalXS,
    },
    iconButton: {
        color: customColors.white,
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        minWidth: 'unset',
        ':hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
        },
    },
});

interface EditChatNameProps {
    name: string;
    onSave: (newTitle: string) => Promise<void>;
    onCancel: () => void;
}

export const EditChatName: React.FC<EditChatNameProps> = ({ name, onSave, onCancel }) => {
    const styles = useStyles();
    const [title, setTitle] = useState<string>(name);

    useEffect(() => {
        setTitle(name);
    }, [name]);

    const onTitleChange = (_ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setTitle(data.value);
    };

    const handleSave = async () => {
        const nextTitle = title.trim();
        if (!nextTitle || nextTitle === name) {
            onCancel();
            return;
        }

        await onSave(nextTitle);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void handleSave();
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
        }
    };

    return (
        <div className={styles.root} onClick={(event) => event.stopPropagation()}>
            <Input className={styles.input} value={title} onChange={onTitleChange} onKeyDown={handleKeyDown} />
            <div className={styles.buttons}>
                <Button
                    className={styles.iconButton}
                    aria-label="edit_name_cancel"
                    icon={<Dismiss20Filled primaryFill={customColors.white} />}
                    onClick={onCancel}
                    appearance="transparent"
                />
                <Button
                    className={styles.iconButton}
                    aria-label="edit_name_confirm"
                    icon={<Checkmark20Filled primaryFill={customColors.white} />}
                    onClick={() => void handleSave()}
                    appearance="transparent"
                />
            </div>
        </div>
    );
};

export default EditChatName;
